import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, CheckCircle, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function EmployeeSessionPage() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [fatalError, setFatalError] = useState('');
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [done, setDone] = useState(false);
  const messagesEndRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/session/${token}`);
      setSession(data.session);
      setMessages(data.messages || []);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Link ungültig oder abgelaufen';
      setFatalError(detail);
    }
    setLoading(false);
  }, [token]);

  // Initial-Load + Auto-Start
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/session/${token}`);
        if (data.session.status === 'unlocked') {
          const { data: started } = await axios.post(`${API}/public/session/${token}/start`);
          setSession(started.session);
          setMessages(started.messages || []);
        } else {
          setSession(data.session);
          setMessages(data.messages || []);
        }
      } catch (err) {
        const detail = err.response?.data?.detail || 'Link ungültig oder abgelaufen';
        setFatalError(detail);
      }
      setLoading(false);
    })();
  }, [token]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true); setError('');
    setMessages((prev) => [...prev, { id: 'tmp-' + Date.now(), sender: 'user', message_text: text, created_at: new Date().toISOString() }]);
    setInput('');
    try {
      await axios.post(`${API}/public/session/${token}/messages`, { message: text });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Nachricht konnte nicht gesendet werden');
      await load();
    }
    setSending(false);
  };

  const complete = async () => {
    setCompleting(true); setError('');
    try {
      await axios.post(`${API}/public/session/${token}/complete`);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Konnte Session nicht abschließen');
    }
    setCompleting(false);
  };

  // FATAL: ungültiger oder abgelaufener Link
  if (fatalError) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="bg-white p-8 max-w-[460px] w-full text-center">
          <AlertTriangle size={48} className="mx-auto text-red-500 mb-3" />
          <h1 className="text-xl font-bold text-[#1f1f1f] mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Zugang nicht möglich
          </h1>
          <p className="text-sm text-gray-600 mb-4">{fatalError}</p>
          <p className="text-xs text-gray-400">
            Bitte beim Vorgesetzten einen neuen Link anfordern oder uns kontaktieren:<br />
            <a href="mailto:hallo@enbet-consulting.de" className="text-[#658a07] hover:underline">hallo@enbet-consulting.de</a>
          </p>
        </div>
      </div>
    );
  }

  // DONE: nach Complete
  if (done) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6">
        <div className="bg-white p-8 max-w-[460px] w-full text-center">
          <CheckCircle size={48} className="mx-auto text-[#658a07] mb-3" />
          <h1 className="text-xl font-bold text-[#1f1f1f] mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Vielen Dank
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            Ihre Antworten wurden gespeichert. Ihr Vorgesetzter sieht sie nun und gibt frei, was im Auswertungs-Report landet.
          </p>
          <p className="text-xs text-gray-400">
            Sie können dieses Fenster jetzt schließen.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  const remaining = session ? Math.max(0, session.max_user_messages - Math.floor((session.message_count || 0) / 2)) : 0;

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col">
      {/* Header */}
      <header className="bg-[#0a0a0a] text-white px-5 py-4">
        <div className="max-w-[820px] mx-auto flex items-start justify-between">
          <div>
            <p className="text-[#befa0a] text-[10px] uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
              EnBet · Chatbot-Session
            </p>
            <h1 className="text-lg md:text-xl font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {session?.domain_label || ''}
            </h1>
            {session?.delegated_to_name && (
              <p className="text-[11px] text-gray-400 mt-0.5">Hallo {session.delegated_to_name}</p>
            )}
            <p className="text-[10px] text-gray-400 mt-1">Noch {remaining} Nachrichten möglich</p>
          </div>
        </div>
      </header>

      {error && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 p-3 text-sm max-w-[820px] w-full mx-auto">
          <AlertTriangle size={14} className="inline mr-2" /> {error}
        </div>
      )}

      {/* Chat */}
      <main className="flex-1 overflow-y-auto px-4 md:px-6 py-6">
        <div className="max-w-[820px] mx-auto space-y-3">
          {messages.length === 0 ? (
            <div className="text-center py-12 text-gray-400 italic">Einen Moment, ich bereite deine erste Frage vor…</div>
          ) : (
            messages.map((m) => <EmployeeChatBubble key={m.id} message={m} />)
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Eingabe */}
      <footer className="border-t border-gray-200 bg-white p-3 md:p-4">
        <div className="max-w-[820px] mx-auto">
          {confirmComplete ? (
            <div className="bg-amber-50 border border-amber-300 p-3 rounded">
              <p className="text-sm text-amber-900 mb-3">
                Wirklich abschließen? Du kannst danach keine Antworten mehr ändern.
                Dein Vorgesetzter sieht dann was du eingegeben hast.
              </p>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setConfirmComplete(false)}
                  className="text-xs uppercase tracking-wider px-3 py-2 text-gray-500 hover:text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Weiter chatten
                </button>
                <button onClick={complete} disabled={completing}
                  className="bg-[#658a07] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#496306] disabled:opacity-50 inline-flex items-center gap-2"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {completing ? <><Loader2 size={12} className="animate-spin" /> Speichere…</> : <><CheckCircle size={14} /> Ja, abschließen</>}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 items-end">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Antwort eingeben… (Enter senden, Shift+Enter für neue Zeile)"
                rows={2}
                disabled={sending}
                className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a] resize-none"
              />
              <div className="flex flex-col gap-2">
                <button onClick={send} disabled={sending || !input.trim()}
                  className="bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-1.5"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
                <button onClick={() => setConfirmComplete(true)} disabled={sending || messages.length < 4}
                  title={messages.length < 4 ? 'Erst ein paar Antworten geben' : 'Session abschließen'}
                  className="border border-gray-300 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Fertig
                </button>
              </div>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
}

function EmployeeChatBubble({ message }) {
  const isUser = message.sender === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] md:max-w-[70%] px-4 py-2.5 rounded-lg ${
        isUser ? 'bg-[#0a0a0a] text-white' : 'bg-white border border-gray-200 text-[#1f1f1f]'
      }`}>
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.message_text}</p>
      </div>
    </div>
  );
}
